// ── myPT DATABASE ─────────────────────────────────────────────────────────────
// Hybrid localStorage + Supabase database.
// localStorage is the primary cache (synchronous reads, instant UX).
// Supabase syncs in the background — writes go to both, initDB pulls from cloud.
// All existing exports remain unchanged — zero component changes needed.

import { supabase } from './supabase.js';

const DB_KEY = "myptdb_v2";

// ── TABLE NAME MAPPING ────────────────────────────────────────────────────────
// Maps localStorage table keys → Supabase table names
const TABLE_MAP = {
  pts:              "pts",
  clients:          "clients",
  sessions:         "sessions",
  upcomingSessions: "upcoming_sessions",
  plans:            "plans",
  exercises:        "exercises",
  messages:         "messages",
  contacts:         "contacts",
};

// ── SUPABASE FIELD NORMALISATION ─────────────────────────────────────────────
// Supabase uses snake_case; localStorage uses camelCase.
// These functions convert between the two for the tables that differ.

function toSupabase(table, item) {
  if (table === "upcomingSessions") {
    const { clientId, ptId, planId, ...rest } = item;
    const out = { ...rest };
    if (clientId !== undefined) out.client_id = clientId;
    if (ptId !== undefined)     out.pt_id = ptId;
    if (planId !== undefined)   out.plan_id = planId;
    return out;
  }
  if (table === "sessions") {
    const { clientId, ptId, planId, coachNotes, clientComment, clientRating, ...rest } = item;
    const out = { ...rest };
    if (clientId      !== undefined) out.client_id      = clientId;
    if (ptId          !== undefined) out.pt_id          = ptId;
    if (planId        !== undefined) out.plan_id        = planId;
    if (coachNotes    !== undefined) out.coach_notes    = coachNotes;
    if (clientComment !== undefined) out.client_comment = clientComment;
    if (clientRating  !== undefined) out.client_rating  = clientRating;
    return out;
  }
  if (table === "exercises") {
    const { bodyArea, teachingPoints, videoUrl, inLibrary, ptId, ...rest } = item;
    const out = { ...rest };
    if (bodyArea       !== undefined) out.body_area       = bodyArea;
    if (teachingPoints !== undefined) out.teaching_points = teachingPoints;
    if (videoUrl       !== undefined) out.video_url       = videoUrl;
    if (inLibrary      !== undefined) out.in_library      = inLibrary;
    if (ptId           !== undefined) out.pt_id           = ptId;
    return out;
  }
  if (table === "clients") {
    const { ptId, ...rest } = item;
    const out = { ...rest };
    if (ptId !== undefined) out.pt_id = ptId;
    return out;
  }
  if (table === "plans") {
    const { ptId, ...rest } = item;
    const out = { ...rest };
    if (ptId !== undefined) out.pt_id = ptId;
    return out;
  }
  if (table === "contacts") {
    const { ptId, contactPref, bestTime, ...rest } = item;
    const out = { ...rest };
    if (ptId        !== undefined) out.pt_id        = ptId;
    if (contactPref !== undefined) out.contact_pref = contactPref;
    if (bestTime    !== undefined) out.availability = bestTime;
    return out;
  }
  return item;
}

function fromSupabase(table, row) {
  if (table === "upcomingSessions") {
    const { client_id, pt_id, plan_id, created_at, ...rest } = row;
    const out = { ...rest };
    if (client_id !== undefined) out.clientId = client_id;
    if (pt_id     !== undefined) out.ptId     = pt_id;
    if (plan_id   !== undefined) out.planId   = plan_id;
    return out;
  }
  if (table === "sessions") {
    const { client_id, pt_id, plan_id, coach_notes, client_comment, client_rating, created_at, ...rest } = row;
    const out = { ...rest };
    if (client_id      !== undefined) out.clientId      = client_id;
    if (pt_id          !== undefined) out.ptId          = pt_id;
    if (plan_id        !== undefined) out.planId        = plan_id;
    if (coach_notes    !== undefined) out.coachNotes    = coach_notes;
    if (client_comment !== undefined) out.clientComment = client_comment;
    if (client_rating  !== undefined) out.clientRating  = client_rating;
    return out;
  }
  if (table === "exercises") {
    const { body_area, teaching_points, video_url, in_library, pt_id, created_at, ...rest } = row;
    const out = { ...rest };
    if (body_area       !== undefined) out.bodyArea       = body_area;
    if (teaching_points !== undefined) out.teachingPoints = teaching_points;
    if (video_url       !== undefined) out.videoUrl       = video_url;
    if (in_library      !== undefined) out.inLibrary      = in_library;
    if (pt_id           !== undefined) out.ptId           = pt_id;
    return out;
  }
  if (table === "clients") {
    const { pt_id, created_at, ...rest } = row;
    const out = { ...rest };
    if (pt_id !== undefined) out.ptId = pt_id;
    return out;
  }
  if (table === "plans") {
    const { pt_id, created_at, ...rest } = row;
    const out = { ...rest };
    if (pt_id !== undefined) out.ptId = pt_id;
    if (Array.isArray(rest.exercises)) out.exercises = rest.exercises;
    return out;
  }
  if (table === "contacts") {
    const { pt_id, contact_pref, availability, created_at, ...rest } = row;
    const out = { ...rest };
    if (pt_id        !== undefined) out.ptId        = pt_id;
    if (contact_pref !== undefined) out.contactPref = contact_pref;
    if (availability !== undefined) out.bestTime    = availability;
    return out;
  }
  // pts, messages — minimal transform
  const { created_at, trial_start, ...rest } = row;
  return rest;
}

// ── BACKGROUND SUPABASE SYNC ──────────────────────────────────────────────────
// Fire-and-forget helpers — errors are logged but never throw.

async function _sbInsert(table, item) {
  const sbTable = TABLE_MAP[table];
  if (!sbTable) return;
  const payload = toSupabase(table, item);
  const { error } = await supabase.from(sbTable).insert(payload);
  if (error) console.warn(`[myPT Supabase] insert ${sbTable}:`, error.message);
}

async function _sbUpdate(table, id, patch) {
  const sbTable = TABLE_MAP[table];
  if (!sbTable) return;
  const payload = toSupabase(table, patch);
  delete payload.id;
  const { error } = await supabase.from(sbTable).update(payload).eq("id", id);
  if (error) console.warn(`[myPT Supabase] update ${sbTable}:`, error.message);
}

async function _sbDelete(table, id) {
  const sbTable = TABLE_MAP[table];
  if (!sbTable) return;
  const { error } = await supabase.from(sbTable).delete().eq("id", id);
  if (error) console.warn(`[myPT Supabase] delete ${sbTable}:`, error.message);
}

// ── SEED DATA ─────────────────────────────────────────────────────────────────
const SEED = {
  pts: [
    { id: 1,   name: "Percy Trena",       gender: "M", rate: 40, location: "Clapham",          specialism: "Muscular Sculpting",       membership: "NRPT", level: 3, experience: "Four years in a variety of London gyms. Passionate about body composition and helping clients build lasting strength habits.", qualifications: ["NVQ Level 3", "YMCA Personal Trainer", "BSc Sports Science"], instagram: "https://www.instagram.com/p/C9example/", youtube: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", tiktok: "@percyfit", facebook: "https://facebook.com/percytranser", linkedin: "https://linkedin.com/in/percytranser" },
    { id: 2,   name: "Joanne Fitzmore",      gender: "F", rate: 50, location: "Islington",         specialism: "Post-natal Fitness",       membership: "REPS", level: 3, experience: "Specialist in ante and post-natal exercise. Helping mums reclaim their strength safely and confidently.", qualifications: ["YMCA Personal Trainer", "Ante/Post-natal Specialist", "REPs Level 3"] },
    { id: 3,   name: "Marcus Williams",      gender: "M", rate: 55, location: "Brixton",           specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "Former sprinter turned S&C coach. I bring elite athletic principles to everyday clients looking for real results.", qualifications: ["REPs Level 4", "BSc Sports Science", "UKSCA Accredited"] },
    { id: 4,   name: "Sophie Clarke",        gender: "F", rate: 45, location: "Chelsea",           specialism: "Weight Loss",             membership: "NRPT", level: 3, experience: "Six years helping clients lose weight sustainably. Nutrition-led approach combined with progressive resistance training.", qualifications: ["REPs Level 3", "Precision Nutrition PN1", "NRPT Certified"] },
    { id: 5,   name: "Emmanuel Osei",        gender: "M", rate: 50, location: "Hackney",           specialism: "HIIT & Cardio Fitness",   membership: "REPS", level: 3, experience: "High energy sessions guaranteed. I specialise in fat-burning HIIT programmes that keep you coming back for more.", qualifications: ["REPs Level 3", "First Aid at Work", "Kettlebell Instructor"] },
    { id: 6,   name: "Amelia Hart",          gender: "F", rate: 60, location: "Notting Hill",      specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Over a decade transforming bodies and mindsets. My holistic method covers training, nutrition, sleep and recovery.", qualifications: ["REPs Level 4", "BSc Nutrition", "NASM Certified"] },
    { id: 7,   name: "Kwame Mensah",         gender: "M", rate: 40, location: "Peckham",           specialism: "Boxing Fitness",          membership: "YMCA", level: 3, experience: "Semi-pro boxing background. My pad work sessions are intense, fun and great for overall fitness and confidence.", qualifications: ["YMCA PT Level 3", "England Boxing Coach", "First Aid"] },
    { id: 8,   name: "Lauren Scott",         gender: "F", rate: 45, location: "Wandsworth",        specialism: "Pilates & Core Strength", membership: "REPS", level: 3, experience: "Pilates-informed personal training that builds deep core strength, improves posture and prevents injury.", qualifications: ["REPs Level 3", "STOTT Pilates Instructor", "Anatomy & Physiology"] },
    { id: 9,   name: "Ryan Okafor",          gender: "M", rate: 35, location: "Stratford",         specialism: "Athletic Performance",    membership: "REPS", level: 2, experience: "Young passionate coach with a background in football conditioning. Great with teens and young adults.", qualifications: ["REPs Level 2", "Football Association Coach", "Sports First Aid"] },
    { id: 10,  name: "Natasha Brennan",      gender: "F", rate: 55, location: "Fulham",            specialism: "Functional Fitness",      membership: "NRPT", level: 3, experience: "Movement-based training that translates to real life. I help clients move better, feel better and perform better daily.", qualifications: ["NRPT Level 3", "FMS Certified", "TRX Instructor"] },
    { id: 11,  name: "Tariq Hassan",         gender: "M", rate: 50, location: "Whitechapel",       specialism: "Powerlifting",            membership: "REPS", level: 3, experience: "Competitive powerlifter coaching clients in the big three lifts. Technique-first approach for safe, impressive gains.", qualifications: ["REPs Level 3", "British Powerlifting Coach", "BSc Exercise Science"] },
    { id: 12,  name: "Grace Adeyemi",        gender: "F", rate: 40, location: "Lewisham",          specialism: "Weight Loss",             membership: "YMCA", level: 3, experience: "Empathetic, supportive trainer helping women lose weight without the fads. Real food, real exercise, real results.", qualifications: ["YMCA PT Level 3", "Nutrition for Sport", "REPs Registered"] },
    { id: 13,  name: "Connor O'Brien",       gender: "M", rate: 45, location: "Hammersmith",       specialism: "Sports Recovery",         membership: "REPS", level: 3, experience: "Injury rehabilitation and return-to-sport specialist. Former physio assistant with a strong clinical background.", qualifications: ["REPs Level 3", "Sports Massage Therapist", "Injury Rehab Diploma"] },
    { id: 14,  name: "Priya Sharma",         gender: "F", rate: 50, location: "Kensington",        specialism: "Yoga & Flexibility",      membership: "REPS", level: 3, experience: "Combining yoga methodology with personal training science. I improve flexibility, balance and mental wellbeing alongside strength.", qualifications: ["REPs Level 3", "200hr RYT Yoga Teacher", "Mindfulness Coach"] },
    { id: 15,  name: "Leon Campbell",        gender: "M", rate: 60, location: "Mayfair",           specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "Worked with professional athletes and executives at top London facilities. High-performance training tailored to demanding lifestyles.", qualifications: ["REPs Level 4", "UKSCA S&C Coach", "MSc Sports Science"] },
    { id: 16,  name: "Aoife Murphy",         gender: "F", rate: 35, location: "Dalston",           specialism: "Cardio Fitness",          membership: "NRPT", level: 2, experience: "Enthusiastic and motivating — I make cardio actually enjoyable! Great for beginners wanting a fun introduction to fitness.", qualifications: ["NRPT Level 2", "Exercise to Music", "First Aid"] },
    { id: 17,  name: "Darius Okonkwo",       gender: "M", rate: 45, location: "Bethnal Green",     specialism: "Muscle Building",         membership: "REPS", level: 3, experience: "Natural bodybuilding enthusiast with eight years of training experience. I simplify hypertrophy science for consistent gains.", qualifications: ["REPs Level 3", "Advanced Resistance Training", "Sports Nutrition"] },
    { id: 18,  name: "Hannah Webb",          gender: "F", rate: 55, location: "Richmond",          specialism: "Senior Fitness",          membership: "REPS", level: 3, experience: "Specialist in training adults 60+ safely and effectively. Improving balance, strength and independence in later life.", qualifications: ["REPs Level 3", "Later Life Training Specialist", "Falls Prevention Instructor"] },
    { id: 19,  name: "Liam Fitzgerald",      gender: "M", rate: 40, location: "Tooting",           specialism: "HIIT & Cardio Fitness",   membership: "YMCA", level: 3, experience: "Boot camp specialist who makes group and 1-2-1 sessions brutally effective. You will work hard but love every session.", qualifications: ["YMCA PT Level 3", "Boot Camp Instructor", "Athletics Coach"] },
    { id: 20,  name: "Kezia Mensah-Bonsu",   gender: "F", rate: 50, location: "Camberwell",        specialism: "Post-natal Fitness",      membership: "REPS", level: 3, experience: "Mother of two, specialist in post-natal recovery. I understand the journey first-hand and tailor every programme with care.", qualifications: ["REPs Level 3", "Post-natal Exercise Specialist", "Nutrition Diploma"] },
    { id: 21,  name: "Arjun Patel",          gender: "M", rate: 45, location: "Wembley",           specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Helped over 200 clients achieve their weight loss goals through personalised training and realistic nutrition guidance.", qualifications: ["REPs Level 3", "Precision Nutrition PN1", "Wellbeing Coach"] },
    { id: 22,  name: "Chloe Edwards",        gender: "F", rate: 40, location: "Balham",            specialism: "Functional Fitness",      membership: "NRPT", level: 3, experience: "I teach clients to move the way humans were designed to move. Functional training for a functional life — in and out of the gym.", qualifications: ["NRPT Level 3", "FMS Screen Certified", "Kettlebell Instructor"] },
    { id: 23,  name: "Aaron Jeffers",        gender: "M", rate: 50, location: "Shoreditch",        specialism: "Athletic Performance",    membership: "REPS", level: 4, experience: "Track and field background. I use periodised training to unlock genuine athletic performance in everyday people.", qualifications: ["REPs Level 4", "UKA Athletics Coach", "Sports Psychology Diploma"] },
    { id: 24,  name: "Maya Thompson",        gender: "F", rate: 45, location: "Stoke Newington",   specialism: "Body Transformation",     membership: "REPS", level: 3, experience: "Transformations are about more than aesthetics — it's a mindset shift. I coach body, habit and lifestyle together.", qualifications: ["REPs Level 3", "Cognitive Behavioural Coaching", "Nutrition for Fat Loss"] },
    { id: 25,  name: "Kieran Boyle",         gender: "M", rate: 35, location: "Bermondsey",        specialism: "Boxing Fitness",          membership: "YMCA", level: 2, experience: "Club boxer turned fitness coach. I use boxing drills to build fitness, confidence and stress relief without the ring.", qualifications: ["YMCA PT Level 2", "ABA Boxing Coach Level 1", "First Aid"] },
    { id: 26,  name: "Zara Ahmed",           gender: "F", rate: 55, location: "Canary Wharf",      specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "Corporate wellness specialist. I work with busy professionals to fit high-impact training into demanding schedules.", qualifications: ["REPs Level 4", "BSc Exercise Physiology", "Corporate Wellness Coach"] },
    { id: 27,  name: "Devante Morgan",       gender: "M", rate: 45, location: "Tottenham",         specialism: "Muscle Building",         membership: "REPS", level: 3, experience: "Gym owner and coach with a passion for natural bodybuilding. Programming that builds real, sustainable muscle.", qualifications: ["REPs Level 3", "Advanced Nutrition", "British Drug Free Bodybuilding Coach"] },
    { id: 28,  name: "Jessica Park",         gender: "F", rate: 40, location: "Putney",            specialism: "Pilates & Core Strength", membership: "REPS", level: 3, experience: "Combines clinical Pilates with personal training to address back pain, poor posture and core weakness effectively.", qualifications: ["REPs Level 3", "APPI Clinical Pilates", "Postural Assessment Diploma"] },
    { id: 29,  name: "Mohammed Al-Rashid",   gender: "M", rate: 50, location: "Paddington",        specialism: "Powerlifting",            membership: "REPS", level: 3, experience: "Competed at national level in powerlifting. Coaching clients from absolute beginners to competitive athletes.", qualifications: ["REPs Level 3", "IPF Technical Official", "Strength Sport Coach"] },
    { id: 30,  name: "Ellie Gibson",         gender: "F", rate: 45, location: "Greenwich",         specialism: "Cardio Fitness",          membership: "NRPT", level: 3, experience: "Marathon runner with seven years of coaching experience. I make cardiovascular training structured, progressive and achievable.", qualifications: ["NRPT Level 3", "UK Athletics Running Coach", "Sports Nutrition Certificate"] },
    { id: 31,  name: "Ollie Bennett",        gender: "M", rate: 55, location: "Battersea",         specialism: "Functional Fitness",      membership: "REPS", level: 3, experience: "Outdoor and functional training specialist. I use parks, stairs and bodyweight to deliver workouts with a difference.", qualifications: ["REPs Level 3", "Outdoor Fitness Instructor", "Calisthenics Coach"] },
    { id: 32,  name: "Adaeze Eze",           gender: "F", rate: 50, location: "Elephant & Castle", specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Evidence-based fat loss coach. I cut through the noise and give clients a programme that actually works — no gimmicks.", qualifications: ["REPs Level 3", "Precision Nutrition PN2", "Behaviour Change Coach"] },
    { id: 33,  name: "Finn MacAulay",        gender: "M", rate: 40, location: "Camden",            specialism: "Sports Recovery",         membership: "REPS", level: 3, experience: "Ex-rugby player turned rehab specialist. I get injured athletes back on the pitch and everyday clients out of pain.", qualifications: ["REPs Level 3", "RFU Community Coach", "Sports Injury Management"] },
    { id: 34,  name: "Olivia Hurst",         gender: "F", rate: 60, location: "South Kensington",  specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Ten years transforming high-end clients. Results-driven, no-nonsense approach with premium service and complete discretion.", qualifications: ["REPs Level 4", "NASM CPT", "Precision Nutrition PN2"] },
    { id: 35,  name: "Carlton Brown",        gender: "M", rate: 45, location: "Vauxhall",          specialism: "HIIT & Cardio Fitness",   membership: "YMCA", level: 3, experience: "Bootcamp and HIIT enthusiast who has trained hundreds of clients in South London. Energy and results guaranteed.", qualifications: ["YMCA PT Level 3", "Spin Instructor", "Exercise Referral"] },
    { id: 36,  name: "Siobhan Kelly",        gender: "F", rate: 40, location: "Finsbury Park",     specialism: "Senior Fitness",          membership: "REPS", level: 3, experience: "Dedicated to improving quality of life for older adults. Safe, progressive and enjoyable sessions for the 55+ community.", qualifications: ["REPs Level 3", "Later Life Training Diploma", "Chair-based Exercise Instructor"] },
    { id: 37,  name: "Yusuf Diallo",         gender: "M", rate: 45, location: "Walthamstow",       specialism: "Athletic Performance",    membership: "REPS", level: 3, experience: "Speed and agility coach working with football players, sprinters and fitness enthusiasts looking to move faster.", qualifications: ["REPs Level 3", "FA Youth Coach", "Speed & Agility Instructor"] },
    { id: 38,  name: "Molly Keane",          gender: "F", rate: 35, location: "Catford",           specialism: "Post-natal Fitness",      membership: "NRPT", level: 2, experience: "Warm, encouraging trainer specialising in helping new mums regain strength and energy after having their baby.", qualifications: ["NRPT Level 2", "Buggyfit Instructor", "Post-natal Core Specialist"] },
    { id: 39,  name: "Noah Adekunle",        gender: "M", rate: 50, location: "Mile End",          specialism: "Strength & Conditioning",  membership: "REPS", level: 3, experience: "University-level rugby coach now working 1-2-1 with clients who want to build real athletic strength from the ground up.", qualifications: ["REPs Level 3", "RFU Coaching Award", "Strength Sport Diploma"] },
    { id: 40,  name: "Fatima Al-Zahra",      gender: "F", rate: 55, location: "Maida Vale",        specialism: "Yoga & Flexibility",      membership: "REPS", level: 3, experience: "Experienced yoga teacher blending eastern tradition with modern fitness science for complete body and mind wellbeing.", qualifications: ["REPs Level 3", "300hr RYT Advanced Yoga", "Meditation Teacher"] },
    { id: 41,  name: "George Sullivan",      gender: "M", rate: 40, location: "Bow",               specialism: "Muscle Building",         membership: "REPS", level: 3, experience: "Straightforward muscle-building coach. No fads, no supplements — just progressive overload, good technique and enough sleep.", qualifications: ["REPs Level 3", "Advanced Anatomy", "Sports Nutrition Level 3"] },
    { id: 42,  name: "Ruby Osei",            gender: "F", rate: 45, location: "Dulwich",           specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Five years helping women over 35 lose weight and regain confidence through tailored training and sustainable nutrition.", qualifications: ["REPs Level 3", "Women's Health & Fitness Coach", "Nutrition Advisor"] },
    { id: 43,  name: "Hamid Karimi",         gender: "M", rate: 50, location: "Ealing",            specialism: "Boxing Fitness",          membership: "YMCA", level: 3, experience: "Professional boxing coach with a wealth of experience in fitness boxing. Cardio, coordination and serious calorie burn.", qualifications: ["YMCA PT Level 3", "England Boxing Coach Level 2", "Combat Sports Conditioning"] },
    { id: 44,  name: "Niamh O'Sullivan",     gender: "F", rate: 40, location: "Bow",               specialism: "Pilates & Core Strength", membership: "NRPT", level: 3, experience: "Studio Pilates instructor and personal trainer. Expert in building a bulletproof core that supports everything you do.", qualifications: ["NRPT Level 3", "Body Control Pilates Teacher", "Sports Rehabilitation"] },
    { id: 45,  name: "Elijah Clarke",        gender: "M", rate: 35, location: "Wood Green",        specialism: "Cardio Fitness",          membership: "REPS", level: 2, experience: "Passionate young coach helping beginners take their first steps into fitness. Patient, positive and full of energy.", qualifications: ["REPs Level 2", "Community Football Coach", "First Aid at Work"] },
    { id: 46,  name: "Amber Davis",          gender: "F", rate: 65, location: "Wimbledon",         specialism: "Athletic Performance",    membership: "REPS", level: 4, experience: "Tennis academy coach turned performance PT. I use sport science to extract the best from competitive and recreational athletes.", qualifications: ["REPs Level 4", "LTA Tennis Coach", "Sports Science MSc"] },
    { id: 47,  name: "Declan Walsh",         gender: "M", rate: 45, location: "Acton",             specialism: "Sports Recovery",         membership: "REPS", level: 3, experience: "Certified sports massage therapist and personal trainer. I address the root cause of pain and build clients back stronger.", qualifications: ["REPs Level 3", "Sports Massage Diploma", "Dry Needling Practitioner"] },
    { id: 48,  name: "Erin Gallagher",       gender: "F", rate: 50, location: "Shepherd's Bush",   specialism: "Body Transformation",     membership: "REPS", level: 3, experience: "Transformation coach who has changed her own body and now guides others through the same evidence-based process.", qualifications: ["REPs Level 3", "PN Nutrition Coach", "Mindset & Habit Coach"] },
    { id: 49,  name: "Kai Mensah",           gender: "M", rate: 40, location: "Bromley",           specialism: "Functional Fitness",      membership: "NRPT", level: 3, experience: "Parkour practitioner and movement coach. My sessions are unlike anything you've tried — creative, challenging and brilliant fun.", qualifications: ["NRPT Level 3", "Parkour UK Coach", "Animal Flow Instructor"] },
    { id: 50,  name: "Valentina Rossi",      gender: "F", rate: 55, location: "Soho",              specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "Italian-trained S&C coach based in Central London. Elegant, precise programming for clients who take their training seriously.", qualifications: ["REPs Level 4", "NSCA-CSCS", "Olympic Weightlifting Coach"] },
    { id: 51,  name: "Jaden Foster",         gender: "M", rate: 45, location: "Hackney",           specialism: "HIIT & Cardio Fitness",   membership: "REPS", level: 3, experience: "Dance fitness and HIIT fusion sessions that never feel like a chore. I keep clients motivated and constantly challenged.", qualifications: ["REPs Level 3", "Zumba Instructor", "Les Mills BodyCombat"] },
    { id: 52,  name: "Sasha Ivanova",        gender: "F", rate: 50, location: "Queensway",         specialism: "Powerlifting",            membership: "REPS", level: 3, experience: "Women's powerlifting advocate. I help women discover the joy of lifting heavy and smashing strength goals.", qualifications: ["REPs Level 3", "British Powerlifting Technical Official", "Strength & Health Coach"] },
    { id: 53,  name: "Patrick Nolan",        gender: "M", rate: 40, location: "Wapping",           specialism: "Muscle Building",         membership: "YMCA", level: 3, experience: "Dedicated hypertrophy coach. I take the guesswork out of building muscle with scientific programming and real accountability.", qualifications: ["YMCA PT Level 3", "Advanced Resistance Training Diploma", "Sports Nutrition"] },
    { id: 54,  name: "Layla Hussain",        gender: "F", rate: 45, location: "Ilford",            specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Helped over 150 women achieve their body goals. A kind, no-judgement environment where your goals are always front and centre.", qualifications: ["REPs Level 3", "Nutrition Coaching Diploma", "Exercise Referral"] },
    { id: 55,  name: "Ewan MacDonald",       gender: "M", rate: 55, location: "Borough",           specialism: "Sports Recovery",         membership: "REPS", level: 4, experience: "UKSCA-accredited practitioner with clinical experience in elite sport. Bringing those standards to private training in London.", qualifications: ["REPs Level 4", "UKSCA Accredited", "MSc Strength & Conditioning"] },
    { id: 56,  name: "Roisin Byrne",         gender: "F", rate: 40, location: "Streatham",         specialism: "Senior Fitness",          membership: "NRPT", level: 3, experience: "Specialist in healthy ageing. My clients in their 60s, 70s and 80s are some of the most inspirational people I've ever met.", qualifications: ["NRPT Level 3", "Later Life Training Specialist", "Dementia-Friendly Fitness"] },
    { id: 57,  name: "Isaiah Boateng",       gender: "M", rate: 50, location: "Lewisham",          specialism: "Athletic Performance",    membership: "REPS", level: 3, experience: "Former national 400m runner. I coach speed, power and endurance with the technical precision of a track athlete.", qualifications: ["REPs Level 3", "UKA Athletics Coach", "Strength & Conditioning Diploma"] },
    { id: 58,  name: "Poppy Lawson",         gender: "F", rate: 45, location: "Marylebone",        specialism: "Yoga & Flexibility",      membership: "REPS", level: 3, experience: "Yin, Vinyasa and restorative yoga integrated with personal training for complete physical and mental balance.", qualifications: ["REPs Level 3", "500hr RYT Yoga Alliance", "Sound Healing Practitioner"] },
    { id: 59,  name: "Max Okonkwo",          gender: "M", rate: 35, location: "Penge",             specialism: "Boxing Fitness",          membership: "YMCA", level: 2, experience: "Young boxing coach making fitness accessible to everyone. Positive atmosphere, brilliant workouts and serious results.", qualifications: ["YMCA PT Level 2", "England Boxing Coach", "Sports Leadership Award"] },
    { id: 60,  name: "Ngozi Eze-Williams",   gender: "F", rate: 55, location: "Brixton",           specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Transformation specialist with a decade of experience. My approach integrates training, lifestyle and psychological coaching.", qualifications: ["REPs Level 4", "Diploma in Life Coaching", "Precision Nutrition PN2"] },
    { id: 61,  name: "Tom Gallagher",        gender: "M", rate: 50, location: "Clerkenwell",       specialism: "Strength & Conditioning",  membership: "REPS", level: 3, experience: "CrossFit background with elite coaching credentials. I program for performance and enjoyment in equal measure.", qualifications: ["REPs Level 3", "CrossFit L2 Trainer", "Olympic Weightlifting Diploma"] },
    { id: 62,  name: "Daisy Webb",           gender: "F", rate: 40, location: "Clapham",           specialism: "Post-natal Fitness",      membership: "REPS", level: 3, experience: "Certified post-natal specialist with a gentle, progressive approach to rebuilding core strength and fitness after birth.", qualifications: ["REPs Level 3", "Mutu Method Practitioner", "Diastasis Recti Specialist"] },
    { id: 63,  name: "Angus MacLeod",        gender: "M", rate: 45, location: "Pimlico",           specialism: "Functional Fitness",      membership: "NRPT", level: 3, experience: "Former military physical training instructor. Disciplined, structured sessions that build genuine functional strength.", qualifications: ["NRPT Level 3", "APTC Military PT", "Level 3 Gym Instructor"] },
    { id: 64,  name: "Pearl Adeyemi",        gender: "F", rate: 60, location: "Kensington",        specialism: "Pilates & Core Strength", membership: "REPS", level: 4, experience: "Body Control Pilates master teacher. I work with clients recovering from surgery, managing chronic pain and seeking elite core strength.", qualifications: ["REPs Level 4", "Body Control Pilates Master Teacher", "Rehabilitation Specialist"] },
    { id: 65,  name: "Callum Reid",          gender: "M", rate: 40, location: "Barking",           specialism: "HIIT & Cardio Fitness",   membership: "REPS", level: 3, experience: "Outdoor and indoor HIIT specialist. Accessible, energetic training for anyone who wants to improve their cardio and lose body fat.", qualifications: ["REPs Level 3", "Outdoor Fitness Instructor", "Exercise to Music"] },
    { id: 66,  name: "Ananya Krishnan",      gender: "F", rate: 50, location: "Southwark",         specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Clinical weight management specialist. I work with clients who have struggled for years to finally achieve lasting fat loss.", qualifications: ["REPs Level 3", "Exercise Referral Level 4", "Obesity & Weight Management"] },
    { id: 67,  name: "Jack Brennan",         gender: "M", rate: 45, location: "Bermondsey",        specialism: "Powerlifting",            membership: "REPS", level: 3, experience: "Powerlifting coach and gym owner. I coach the total — squat, bench and deadlift — with a focus on technique and progression.", qualifications: ["REPs Level 3", "GBPF Affiliated Coach", "Strength Athlete Diploma"] },
    { id: 68,  name: "Irina Volkov",         gender: "F", rate: 55, location: "Barbican",          specialism: "Athletic Performance",    membership: "REPS", level: 4, experience: "Gymnastics background and elite S&C experience. Agility, coordination and explosive power are my speciality.", qualifications: ["REPs Level 4", "British Gymnastics Coach", "NSCA-CSCS"] },
    { id: 69,  name: "Theo Asante",          gender: "M", rate: 40, location: "Catford",           specialism: "Muscle Building",         membership: "YMCA", level: 3, experience: "Dedicated to helping clients build the physique they've always wanted. Methodical, patient coaching with genuine accountability.", qualifications: ["YMCA PT Level 3", "Sports Nutrition Level 3", "Anatomy & Physiology"] },
    { id: 70,  name: "Harriet Blake",        gender: "F", rate: 45, location: "Wimbledon",         specialism: "Senior Fitness",          membership: "REPS", level: 3, experience: "Committed to making later-life fitness enjoyable and meaningful. My clients live independently longer and feel stronger every week.", qualifications: ["REPs Level 3", "Later Life Training", "Nordic Walking Instructor"] },
    { id: 71,  name: "Sam Achebe",           gender: "M", rate: 50, location: "Dalston",           specialism: "Sports Recovery",         membership: "REPS", level: 3, experience: "Physiotherapy-adjacent approach to training. I identify compensation patterns and build clients back from the ground up.", qualifications: ["REPs Level 3", "Clinical Exercise Practitioner", "Sports Therapy Diploma"] },
    { id: 72,  name: "Lily Chambers",        gender: "F", rate: 40, location: "Hackney Wick",      specialism: "Boxing Fitness",          membership: "NRPT", level: 3, experience: "One of London's leading female boxing fitness coaches. My sessions are empowering, intense and deeply satisfying.", qualifications: ["NRPT Level 3", "England Boxing Coach Level 2", "Women's Strength Coach"] },
    { id: 73,  name: "Ben Osei",             gender: "M", rate: 55, location: "Bloomsbury",        specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Academic background in exercise physiology combined with real-world coaching for unmatched transformation results.", qualifications: ["REPs Level 4", "PhD Candidate Exercise Physiology", "Precision Nutrition PN2"] },
    { id: 74,  name: "Tamsin Cooper",        gender: "F", rate: 40, location: "Rotherhithe",       specialism: "Yoga & Flexibility",      membership: "REPS", level: 3, experience: "Ashtanga practitioner and personal trainer. I help tight, stressed clients open up physically and mentally through movement.", qualifications: ["REPs Level 3", "Ashtanga Yoga Teacher", "Mobility & Flexibility Specialist"] },
    { id: 75,  name: "Jordan Campbell",      gender: "M", rate: 45, location: "Stoke Newington",   specialism: "HIIT & Cardio Fitness",   membership: "YMCA", level: 3, experience: "Running coach and fitness trainer who builds aerobic capacity the right way. Smart progressions, no unnecessary injury.", qualifications: ["YMCA PT Level 3", "UK Athletics Run Leader", "Sports Massage Diploma"] },
    { id: 76,  name: "Katarina Nowak",       gender: "F", rate: 55, location: "Chelsea",           specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "Polish national team athletics coach now based in London. Bringing championship-level coaching to private clients.", qualifications: ["REPs Level 4", "IAAF Coaching Diploma", "Sports Science Degree"] },
    { id: 77,  name: "Cian Murphy",          gender: "M", rate: 40, location: "Harrow",            specialism: "Functional Fitness",      membership: "REPS", level: 3, experience: "Injury-prevention specialist who takes a whole-body approach. I help desk workers and athletes move without pain.", qualifications: ["REPs Level 3", "FMS & SFMA Certified", "Corrective Exercise Specialist"] },
    { id: 78,  name: "Fiona MacGregor",      gender: "F", rate: 45, location: "Islington",         specialism: "Weight Loss",             membership: "NRPT", level: 3, experience: "Sustainable weight loss without suffering. I build healthy habits and realistic programmes that fit into your real life.", qualifications: ["NRPT Level 3", "Health Coach Certification", "Weight Management Advisor"] },
    { id: 79,  name: "Zac Williams",         gender: "M", rate: 50, location: "Shoreditch",        specialism: "Powerlifting",            membership: "REPS", level: 3, experience: "Powerlifting purist who believes everyone should experience the joy of lifting really heavy things. Come learn the lifts.", qualifications: ["REPs Level 3", "IPF Technical Official", "BDFPA Certified Coach"] },
    { id: 80,  name: "Eilidh Fraser",        gender: "F", rate: 40, location: "Tooting",           specialism: "Cardio Fitness",          membership: "REPS", level: 3, experience: "Triathlete and endurance coach. Build the aerobic engine you've always wanted with structured, periodised cardio programming.", qualifications: ["REPs Level 3", "British Triathlon Coach", "Open Water Swimming Instructor"] },
    { id: 81,  name: "Olumide Abiodun",      gender: "M", rate: 60, location: "Canary Wharf",      specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Premium PT for results-focused professionals. Time-efficient, evidence-based training designed around busy London lifestyles.", qualifications: ["REPs Level 4", "NASM Master Trainer", "Executive Health Coach"] },
    { id: 82,  name: "Rose Flanagan",        gender: "F", rate: 50, location: "Fulham",            specialism: "Post-natal Fitness",      membership: "REPS", level: 3, experience: "Leading post-natal specialist in SW London. From 6-week clearance to race day — I support mums at every stage.", qualifications: ["REPs Level 3", "Mutu Method", "Ante-natal Pilates Teacher"] },
    { id: 83,  name: "Tyler Barnes",         gender: "M", rate: 45, location: "Walthamstow",       specialism: "Muscle Building",         membership: "REPS", level: 3, experience: "Natural bodybuilding coach who demystifies training for regular people. Great results without extreme methods or supplements.", qualifications: ["REPs Level 3", "Advanced Hypertrophy Programme Design", "Sports Nutrition"] },
    { id: 84,  name: "Morag Campbell",       gender: "F", rate: 55, location: "Richmond",          specialism: "Athletic Performance",    membership: "REPS", level: 4, experience: "Performance director at a leading London running club. Private coaching available for serious runners and triathletes.", qualifications: ["REPs Level 4", "UKA Athletics Coach Level 3", "Sport & Exercise Psychology"] },
    { id: 85,  name: "Shaun Edwards",        gender: "M", rate: 40, location: "Lewisham",          specialism: "Boxing Fitness",          membership: "YMCA", level: 3, experience: "Former amateur boxing champion offering white-collar fitness boxing. Incredible workouts that build character and fitness.", qualifications: ["YMCA PT Level 3", "England Boxing Coach Level 2", "Conditioning Coach Diploma"] },
    { id: 86,  name: "Violet Kessler",       gender: "F", rate: 50, location: "Notting Hill",      specialism: "Pilates & Core Strength", membership: "REPS", level: 4, experience: "STOTT Pilates master instructor with 15 years experience. I work with pre and post-surgical clients and elite athletes alike.", qualifications: ["REPs Level 4", "STOTT Pilates Master Instructor", "Functional Anatomy for Movement"] },
    { id: 87,  name: "Michael Asare",        gender: "M", rate: 45, location: "Hackney",           specialism: "Sports Recovery",         membership: "REPS", level: 3, experience: "Athletic trainer with clinical sports therapy background. I specialise in getting stubborn injuries resolved and athletes performing again.", qualifications: ["REPs Level 3", "Sports Therapy BSc", "Acupuncture & Dry Needling"] },
    { id: 88,  name: "Aleksandra Kowalska",  gender: "F", rate: 45, location: "Hammersmith",       specialism: "Senior Fitness",          membership: "NRPT", level: 3, experience: "Caring and knowledgeable trainer who specialises in keeping older adults strong, mobile and happy. A truly rewarding career.", qualifications: ["NRPT Level 3", "Falls Management Diploma", "Older Adult Physical Activity"] },
    { id: 89,  name: "Isaiah Okafor",        gender: "M", rate: 50, location: "Stratford",         specialism: "HIIT & Cardio Fitness",   membership: "REPS", level: 3, experience: "Fitness content creator and personal trainer. I make sessions engaging, educational and highly effective every single time.", qualifications: ["REPs Level 3", "Kettlebell Level 2", "Nutrition for Performance"] },
    { id: 90,  name: "Tanya Morrison",       gender: "F", rate: 40, location: "Balham",            specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Practical, no-nonsense fat loss specialist. I give clients a programme they can actually stick to — and the results to match.", qualifications: ["REPs Level 3", "Weight Management Specialist", "Exercise Behaviour Change"] },
    { id: 91,  name: "Rajesh Patel",         gender: "M", rate: 45, location: "Southall",          specialism: "Yoga & Flexibility",      membership: "REPS", level: 3, experience: "Hatha and Iyengar-trained yoga specialist integrating traditional Indian practice with contemporary personal training science.", qualifications: ["REPs Level 3", "Iyengar Yoga Teacher", "Sports Flexibility Specialist"] },
    { id: 92,  name: "Natalia Petrov",       gender: "F", rate: 60, location: "Mayfair",           specialism: "Body Transformation",     membership: "REPS", level: 4, experience: "Elite transformation coach serving Mayfair and Knightsbridge clients. Discrete, premium and profoundly effective programmes.", qualifications: ["REPs Level 4", "NASM Certified", "Diploma in Clinical Nutrition"] },
    { id: 93,  name: "Seamus Quinn",         gender: "M", rate: 50, location: "Hammersmith",       specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "UKSCA practitioner with a background coaching Premiership rugby. Every client gets a programme worthy of a professional.", qualifications: ["REPs Level 4", "UKSCA Accredited Practitioner", "RFU Strength Coach"] },
    { id: 94,  name: "Pippa Rhodes",         gender: "F", rate: 45, location: "Wandsworth",        specialism: "Functional Fitness",      membership: "NRPT", level: 3, experience: "Movement educator and personal trainer. I help clients build a body that can do anything — sport, adventure and everyday life.", qualifications: ["NRPT Level 3", "Animal Flow Instructor", "Clubbell & Steel Mace Coach"] },
    { id: 95,  name: "Jordan Mensah",        gender: "M", rate: 40, location: "Peckham",           specialism: "Muscle Building",         membership: "REPS", level: 3, experience: "Grassroots bodybuilding coach passionate about making physique goals accessible. Supportive, science-backed and results-driven.", qualifications: ["REPs Level 3", "Advanced Training Methods", "ISSN Sports Nutrition"] },
    { id: 96,  name: "Shreya Nair",          gender: "F", rate: 55, location: "Fitzrovia",         specialism: "Strength & Conditioning",  membership: "REPS", level: 4, experience: "MSc Sport & Exercise Science. I bring research-level rigour to my coaching — always evidence-based, always effective.", qualifications: ["REPs Level 4", "MSc Sport & Exercise Science", "UKSCA Certified"] },
    { id: 97,  name: "Carlton James",        gender: "M", rate: 45, location: "Battersea",         specialism: "Boxing Fitness",          membership: "YMCA", level: 3, experience: "Professional boxing trainer working with recreational boxers and fitness enthusiasts. Technique, conditioning and results.", qualifications: ["YMCA PT Level 3", "ABA Boxing Level 2", "Padwork & Mitts Specialist"] },
    { id: 98,  name: "Harriet Stone",        gender: "F", rate: 40, location: "Camberwell",        specialism: "Cardio Fitness",          membership: "REPS", level: 3, experience: "Cycling and running coach who turns cardio-haters into cardio-lovers. Structured endurance programmes that progress week to week.", qualifications: ["REPs Level 3", "British Cycling Level 2", "UK Athletics Run Leader"] },
    { id: 99,  name: "David Achampong",      gender: "M", rate: 55, location: "Tottenham",         specialism: "Athletic Performance",    membership: "REPS", level: 4, experience: "University-level sprint coach now training private clients. Explosive power, speed and agility you didn't think you had.", qualifications: ["REPs Level 4", "UKA Sprint Coach", "Plyometric Training Specialist"] },
    { id: 100, name: "Moira Gallagher",      gender: "F", rate: 45, location: "Islington",         specialism: "Post-natal Fitness",      membership: "REPS", level: 3, experience: "Women's wellness coach specialising in the fourth trimester and beyond. Holistic recovery for mind and body after pregnancy.", qualifications: ["REPs Level 3", "Women's Health Coach", "Pelvic Floor & Core Rehab"] },
    { id: 101, name: "Aisha Mohammed",       gender: "F", rate: 50, location: "Ilford",            specialism: "Weight Loss",             membership: "REPS", level: 3, experience: "Compassionate weight loss coach offering a safe, empowering space for women of all backgrounds to reach their health goals.", qualifications: ["REPs Level 3", "Inclusive Fitness Trainer", "Nutrition & Lifestyle Coach"] },
    { id: 102, name: "Kweku Asante",         gender: "M", rate: 40, location: "Woolwich",          specialism: "Muscle Building",         membership: "YMCA", level: 3, experience: "Consistent, progressive hypertrophy coaching for men and women who are serious about building muscle the right way.", qualifications: ["YMCA PT Level 3", "Advanced Anatomy", "Dietary Analysis Diploma"] },
    { id: 103, name: "Elspeth Murray",       gender: "F", rate: 55, location: "Marylebone",        specialism: "Pilates & Core Strength", membership: "REPS", level: 4, experience: "Physiotherapy-informed Pilates specialist. I work with clients managing chronic conditions, post-op recovery and chronic back pain.", qualifications: ["REPs Level 4", "APPI Clinical Pilates Advanced", "Physiotherapy Assistant Diploma"] },
  ],

  clients: [
    {
      id: 1, name: "Clint Fitz", status: "active",
      nextSession: "30/03/2025 - Body Build 19:00", msgs: 2,
      objectives: "Build upper body, stabilise weight at 80kg",
      weight: 85, height: 175, ptId: 1, demo: true,
    },
    {
      id: 2, name: "James Brown", status: "awaiting-notes",
      nextSession: "02/04/2025 - Cardio Burn 17:30", msgs: 0,
      objectives: "Improve cardiovascular fitness, lose 5kg",
      weight: 92, height: 180, ptId: 1, demo: true,
    },
    {
      id: 3, name: "Sarah Mitchell", status: "awaiting-signup",
      nextSession: null, msgs: 1,
      objectives: "Post-natal recovery, tone and strengthen",
      weight: 67, height: 165, ptId: 1, demo: true,
    },
    {
      id: 4, name: "Cloe Fitz", status: "active",
      nextSession: "03/04/2025 - Cardio Burn 18:00", msgs: 0,
      objectives: "Improve cardio fitness and tone up for a 10k run",
      weight: 64, height: 168, ptId: 1, demo: true,
    },
  ],

  appraisals: [
    // Clint Fitz — baseline appraisal (original assessment data) …
    {
      id: 1, clientId: 1, clientName: "Clint Fitz", date: "09/09/2011",
      weight: 66.5, height: 1.6281, anthroCategory: "Above/ average above",
      systolic1: 105, diastolic1: 87, systolic2: 115, diastolic2: 77, systolic3: 113, diastolic3: 77, bpCategory: "",
      bodyFat: 32, bodyFatCategory: "Above/ average above",
      sitReach1: 20, sitReach2: 22, sitReach3: 22, romCategory: "Fair/good",
      vo2: 35.3, vo2Category: "Above/ average above",
      pressUps: 31, muscularCategory: "",
    },
    // … followed by four follow-ups showing steady progress (BMI ↓, body fat ↓, VO₂ ↑, press-ups ↑).
    {
      id: 2, clientId: 1, clientName: "Clint Fitz", date: "21/10/2011",
      weight: 65.6, height: 1.6281, anthroCategory: "Average",
      systolic1: 108, diastolic1: 82, systolic2: 112, diastolic2: 78, systolic3: 110, diastolic3: 76, bpCategory: "Normal",
      bodyFat: 30, bodyFatCategory: "Above average",
      sitReach1: 21, sitReach2: 23, sitReach3: 23, romCategory: "Good",
      vo2: 37.1, vo2Category: "Good",
      pressUps: 34, muscularCategory: "Average",
    },
    {
      id: 3, clientId: 1, clientName: "Clint Fitz", date: "02/12/2011",
      weight: 64.4, height: 1.6281, anthroCategory: "Average",
      systolic1: 110, diastolic1: 80, systolic2: 109, diastolic2: 76, systolic3: 108, diastolic3: 75, bpCategory: "Normal",
      bodyFat: 28, bodyFatCategory: "Average",
      sitReach1: 23, sitReach2: 24, sitReach3: 24, romCategory: "Good",
      vo2: 39.4, vo2Category: "Good",
      pressUps: 38, muscularCategory: "Good",
    },
    {
      id: 4, clientId: 1, clientName: "Clint Fitz", date: "20/01/2012",
      weight: 63.2, height: 1.6281, anthroCategory: "Average",
      systolic1: 106, diastolic1: 78, systolic2: 107, diastolic2: 75, systolic3: 105, diastolic3: 74, bpCategory: "Normal",
      bodyFat: 25, bodyFatCategory: "Average",
      sitReach1: 24, sitReach2: 25, sitReach3: 26, romCategory: "Good",
      vo2: 41.6, vo2Category: "Good",
      pressUps: 43, muscularCategory: "Good",
    },
    {
      id: 5, clientId: 1, clientName: "Clint Fitz", date: "09/03/2012",
      weight: 62.0, height: 1.6281, anthroCategory: "Average",
      systolic1: 104, diastolic1: 76, systolic2: 105, diastolic2: 74, systolic3: 103, diastolic3: 72, bpCategory: "Optimal",
      bodyFat: 22, bodyFatCategory: "Good",
      sitReach1: 26, sitReach2: 27, sitReach3: 27, romCategory: "Excellent",
      vo2: 44.0, vo2Category: "Excellent",
      pressUps: 48, muscularCategory: "Good",
    },
    // Cloe Fitz (female demo client) — two appraisals showing progress.
    {
      id: 6, clientId: 4, clientName: "Cloe Fitz", date: "15/01/2025",
      weight: 66, height: 1.68, anthroCategory: "Average",
      systolic1: 118, diastolic1: 78, systolic2: 120, diastolic2: 80, systolic3: 117, diastolic3: 77, bpCategory: "Normal",
      bodyFat: 28, bodyFatCategory: "Average",
      sitReach1: 22, sitReach2: 24, sitReach3: 24, romCategory: "Good",
      vo2: 38, vo2Category: "Good",
      pressUps: 18, muscularCategory: "Average",
    },
    {
      id: 7, clientId: 4, clientName: "Cloe Fitz", date: "12/03/2025",
      weight: 63.5, height: 1.68, anthroCategory: "Average",
      systolic1: 114, diastolic1: 75, systolic2: 116, diastolic2: 76, systolic3: 113, diastolic3: 74, bpCategory: "Optimal",
      bodyFat: 25, bodyFatCategory: "Good",
      sitReach1: 25, sitReach2: 26, sitReach3: 27, romCategory: "Excellent",
      vo2: 41, vo2Category: "Excellent",
      pressUps: 24, muscularCategory: "Good",
    },
  ],

  contacts: [
    { id: 1, name: "Alex Turner", phone: "0207 123 4567", mobile: "0790 234 5678", email: "alex@email.com", preference: "SMS", bestTime: "Evenings", date: "12/11/2024", contacted: false, ptId: 1 },
    { id: 2, name: "Maria Santos", phone: "0207 987 6543", mobile: "0790 111 2222", email: "maria@email.com", preference: "Mobile", bestTime: "Tuesday AM", date: "12/11/2024", contacted: false, ptId: 1 },
    { id: 3, name: "James Wright", phone: "0207 555 1234", mobile: "0790 444 5555", email: "james@email.com", preference: "Email", bestTime: "Mornings", date: "13/11/2024", contacted: true, ptId: 1 },
  ],

  sessions: [
    // Most recent — PT hasn't reviewed yet → "Awaiting PT feedback"
    { id: 25, date: "28/03/2025", name: "Body Build", time: "19:00", clientId: 1,
      coachNotes: "", clientComment: "", clientRating: 0 },
    // PT has left feedback, waiting on the client → "Awaiting client feedback"
    { id: 24, date: "25/03/2025", name: "Body Build", time: "17:00", clientId: 1,
      coachNotes: "Strong session — chest press technique much improved. Keep your elbows tucked and breathe out on the push.",
      clientComment: "", clientRating: 0 },
    // Both sides have left feedback → "Complete"
    { id: 23, date: "22/03/2025", name: "Body Build", time: "19:00", clientId: 1,
      coachNotes: "Great consistency. Bumped the shoulder press to 12kg and form held up well.",
      clientComment: "Felt strong today — shoulders were tired by the end but in a good way.", clientRating: 5,
      performance: { "Chest press": [{reps:12,kg:30},{reps:10,kg:32},{reps:8,kg:34}], "Shoulder press": [{reps:12,kg:12},{reps:12,kg:14},{reps:10,kg:14}], "Bicep curl": [{reps:12,kg:8},{reps:12,kg:9},{reps:10,kg:10}] } },
    { id: 22, date: "18/03/2025", name: "Anaerobic Mix", time: "18:00", clientId: 1,
      coachNotes: "Pushed hard on the intervals, big effort throughout. Hydrate a bit more before the next one.",
      clientComment: "Tough but really enjoyed it!", clientRating: 4 },
  ],

  upcomingSessions: [
    { id: 101, date: "14/04/2026", name: "Body Build", time: "19:00", clientId: 1 },
    { id: 102, date: "21/04/2026", name: "Cardio Burn", time: "16:00", clientId: 1 },
    { id: 103, date: "05/05/2026", name: "Body Build", time: "18:00", clientId: 1 },
  ],

  plans: [
    {
      id: 1, name: "Body Build — Upper Body", type: "Muscular Development",
      objective: "Build upper body strength and muscle definition through targeted compound and isolation exercises.",
      exercises: ["Treadmill warm up", "Tricep stretch", "Chest press", "Tricep pulley", "Bicep curl", "Shoulder press"],
      ptId: 1,
    },
    {
      id: 2, name: "Cardio Burn — Interval Training", type: "Cardio Fitness",
      objective: "Improve cardiovascular endurance and burn calories with high intensity interval work.",
      exercises: ["Recumbent cycle warm up", "Treadmill intervals", "Rowing machine", "Cross trainer", "Cool down stretch"],
      ptId: 1,
    },
    {
      id: 3, name: "Full Body Strength", type: "Muscular Development",
      objective: "Comprehensive full body session targeting all major muscle groups.",
      exercises: ["Warm up walk", "Squats", "Deadlifts", "Bench press", "Lat pulldown", "Shoulder press", "Planks"],
      ptId: 1,
    },
  ],

  exercises: [
    {
      id: 1, name: "Seated Shoulder Press", bodyArea: "Shoulder", muscle: "Deltoid",
      description: "Sit upright with back supported. Hold dumbbells at shoulder height, palms forward. Press overhead until arms nearly fully extended. Lower with control.",
      teachingPoints: "Keep core braced. Avoid arching the lower back.",
      adaptations: "Can be performed standing for greater core activation.",
      alternatives: ["Standing Shoulder Press", "Overhead Tricep Lift"],
    },
    {
      id: 2, name: "Chest Press", bodyArea: "Chest", muscle: "Pectorals",
      description: "Lie flat on bench. Grip barbell slightly wider than shoulder width. Lower to chest, then press to full extension.",
      teachingPoints: "Retract shoulder blades. Feet flat on floor.",
      adaptations: "Incline/decline variations for upper/lower chest emphasis.",
      alternatives: ["Dumbbell Chest Press", "Cable Crossover"],
      videoUrl: "https://www.youtube.com/watch?v=rT7DgCr-3pg",
    },
    {
      id: 3, name: "Bicep Curl", bodyArea: "Arms", muscle: "Biceps",
      description: "Stand with dumbbells at sides. Curl weights towards shoulders while keeping elbows fixed.",
      teachingPoints: "Do not swing the body. Supinate at the top of the movement.",
      adaptations: "Concentration curl for isolation. Hammer curl for brachialis.",
      alternatives: ["Hammer Curl", "Cable Curl"],
    },
    {
      id: 4, name: "Recumbent Cycle", bodyArea: "Cardio", muscle: "Cardio",
      description: "Set seat so leg is nearly extended at bottom of pedal stroke. Maintain steady cadence.",
      teachingPoints: "Keep back supported. Monitor heart rate.",
      adaptations: "Interval sprints for HIIT. Low resistance for warm up.",
      alternatives: ["Upright Bike", "Cross Trainer"],
    },
  ],

  messages: [
    {
      id: 1,
      subject: "Extra booking this week?",
      participants: ["Percy Trena", "James Brown"],
      thread: [
        { poster: "James Brown",   text: "Hi Percy, would really like an extra session this week if possible — Thursday sometime, 6pm to 9pm?", ts: 1732694200000 },
        { poster: "Percy Trena", text: "James certainly, how does 8pm sound?",       ts: 1732780600000 },
        { poster: "James Brown",   text: "8pm is great, see you at the club.",          ts: 1732866600000 },
      ],
    },
    {
      id: 2,
      subject: "I'm going on holiday in a month",
      participants: ["Percy Trena", "James Brown"],
      thread: [
        { poster: "James Brown",   text: "Hi Percy, just a heads-up — I'll be away from 20 Dec for two weeks. Can we plan around it?", ts: 1732348800000 },
      ],
    },
    {
      id: 3,
      subject: "Friend referral — interested in PT",
      participants: ["Percy Trena", "Clint Fitz"],
      thread: [
        { poster: "Clint Fitz",  text: "Percy — I've got a mate who's keen to start training. Can I pass on your details?", ts: 1733001600000 },
      ],
    },
  ],
};

// ── CORE OPERATIONS ───────────────────────────────────────────────────────────

function _load() {
  try {
    const raw = localStorage.getItem(DB_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _save(data) {
  try {
    localStorage.setItem(DB_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn("myPT DB: localStorage write failed", e);
  }
}

/** Call once at app startup — seeds data on first run, no-op thereafter.
 *  Also kicks off a background Supabase sync to populate localStorage from cloud. */
export function initDB() {
  if (!_load()) { _save(SEED); }
  const db = _load();
  let dirty = false;
  // Migrate messages from old format (no participants array) to new format.
  if (Array.isArray(db.messages) && db.messages.some(m => !Array.isArray(m.participants))) {
    db.messages = SEED.messages;
    dirty = true;
  }
  // Migrate upcomingSessions — replace if all dates are in the past.
  const parseDate = d => { const [dd, mm, yy] = d.split("/"); return new Date(yy, mm - 1, dd); };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (Array.isArray(db.upcomingSessions) && db.upcomingSessions.every(s => parseDate(s.date) < today)) {
    db.upcomingSessions = SEED.upcomingSessions;
    dirty = true;
  }
  // Migrate sessions: rename "complete" status to "rebooked".
  if (Array.isArray(db.sessions) && db.sessions.some(s => s.status === "complete")) {
    db.sessions = db.sessions.map(s => s.status === "complete" ? { ...s, status: "rebooked" } : s);
    dirty = true;
  }
  // Migrate exercises: backfill videoUrl on seed exercises that now have one.
  if (Array.isArray(db.exercises)) {
    const chestPress = db.exercises.find(e => e.id === 2 && !e.videoUrl);
    if (chestPress) {
      db.exercises = db.exercises.map(e => e.id === 2 ? { ...e, videoUrl: "https://www.youtube.com/watch?v=rT7DgCr-3pg" } : e);
      dirty = true;
    }
  }
  // Seed / re-seed the appraisals collection. Bump APPRAISALS_SEED_VERSION when
  // the demo dataset changes so existing installs pick up the new records, while
  // preserving any appraisals a PT entered for their own (non-seed) clients.
  const APPRAISALS_SEED_VERSION = 2;
  if (!Array.isArray(db.appraisals)) {
    db.appraisals = SEED.appraisals || [];
    db.appraisalsSeedVersion = APPRAISALS_SEED_VERSION;
    dirty = true;
  } else if ((db.appraisalsSeedVersion || 0) < APPRAISALS_SEED_VERSION) {
    const seedClientIds = new Set((SEED.appraisals || []).map(a => a.clientId));
    const userAdded = db.appraisals.filter(a => !seedClientIds.has(a.clientId));
    db.appraisals = [...(SEED.appraisals || []), ...userAdded];
    db.appraisalsSeedVersion = APPRAISALS_SEED_VERSION;
    dirty = true;
  }

  // Re-seed demo sessions when the feedback model changes, keeping any the PT
  // has booked themselves (ids outside the seed range).
  const SESSIONS_SEED_VERSION = 1;
  if ((db.sessionsSeedVersion || 0) < SESSIONS_SEED_VERSION) {
    const seedIds = new Set((SEED.sessions || []).map(s => s.id));
    const booked = (db.sessions || []).filter(s => !seedIds.has(s.id));
    db.sessions = [...(SEED.sessions || []), ...booked];
    db.sessionsSeedVersion = SESSIONS_SEED_VERSION;
    dirty = true;
  }

  // One-off: normalise the demo client to "Clint Fitz" (covers older "Yomi
  // Onikosi" / "Cillian Onikosi" caches) and add the female demo client
  // "Cloe Fitz" (+ her appraisals) to any already-cached localStorage.
  if (!db.renamedDemoClientV2) {
    const OLD = ["Yomi Onikosi", "Cillian Onikosi"], TO = "Clint Fitz";
    const fix = n => OLD.includes(n) ? TO : n;
    if (Array.isArray(db.clients))
      db.clients = db.clients.map(c => c.id === 1 ? { ...c, name: TO } : c);
    if (Array.isArray(db.appraisals))
      db.appraisals = db.appraisals.map(a => a.clientId === 1 ? { ...a, clientName: TO } : a);
    if (Array.isArray(db.messages))
      db.messages = db.messages.map(m => ({
        ...m,
        participants: Array.isArray(m.participants) ? m.participants.map(fix) : m.participants,
        thread: Array.isArray(m.thread) ? m.thread.map(t => t.poster && OLD.includes(t.poster) ? { ...t, poster: TO } : t) : m.thread,
      }));
    if (Array.isArray(db.clients) && !db.clients.some(c => c.id === 4))
      db.clients = [...db.clients, ...(SEED.clients || []).filter(c => c.id === 4)];
    if (Array.isArray(db.appraisals) && !db.appraisals.some(a => a.clientId === 4))
      db.appraisals = [...db.appraisals, ...(SEED.appraisals || []).filter(a => a.clientId === 4)];
    db.renamedDemoClientV2 = true;
    dirty = true;
  }

  // Backfill the demo flag on the seed clients (ids 1-4) for already-cached
  // installs, so the DEMO label + "hide demo clients" toggle work everywhere.
  if (Array.isArray(db.clients) && db.clients.some(c => [1, 2, 3, 4].includes(c.id) && c.demo === undefined)) {
    db.clients = db.clients.map(c => [1, 2, 3, 4].includes(c.id) ? { ...c, demo: true } : c);
    dirty = true;
  }

  // Filler demo clients (James Brown id 2, Sarah Mitchell id 3) clutter every
  // trainer's roster. Keep them in LOCAL DEV for testing, but strip them from
  // deployed builds (staging + prod) — NODE_ENV is "development" only on
  // localhost. Covers both fresh seeds and already-cached installs.
  if (process.env.NODE_ENV !== "development" && !db.strippedFillerClientsV1) {
    if (Array.isArray(db.clients))
      db.clients = db.clients.filter(c => c.id !== 2 && c.id !== 3);
    if (Array.isArray(db.messages))
      db.messages = db.messages.filter(m =>
        !(Array.isArray(m.participants) &&
          (m.participants.includes("James Brown") || m.participants.includes("Sarah Mitchell"))));
    db.strippedFillerClientsV1 = true;
    dirty = true;
  }

  if (dirty) _save(db);

  // Background: pull fresh data from Supabase and update localStorage cache.
  // Runs after the synchronous seed/migration so the UI is never blocked.
  _syncFromSupabase();
}

async function _syncFromSupabase() {
  try {
    const tables = Object.keys(TABLE_MAP);
    const fetches = tables.map(lsKey =>
      supabase.from(TABLE_MAP[lsKey]).select("*").then(({ data, error }) => ({ lsKey, data, error }))
    );
    const results = await Promise.all(fetches);
    const db = _load() || { ...SEED };
    let updated = false;
    for (const { lsKey, data, error } of results) {
      if (error) { console.warn(`[myPT Supabase] fetch ${lsKey}:`, error.message); continue; }
      if (Array.isArray(data) && data.length > 0) {
        if (lsKey === "pts") {
          // Merge Supabase rows with SEED: keep local-only fields (social links, etc.)
          // that haven't been added as Supabase columns yet.
          const seedPts = SEED.pts || [];
          db[lsKey] = data.map(row => {
            const mapped = fromSupabase(lsKey, row);
            const seedPt = seedPts.find(s => s.id === mapped.id) || {};
            return { ...seedPt, ...mapped }; // Supabase wins on conflicts; seed fills gaps
          });
        } else if (lsKey === "sessions") {
          // Merge Supabase rows with SEED so seeded feedback (coachNotes / client
          // comment + rating) survives when Supabase has no value for it. Supabase
          // wins only with a real (non-empty) value; otherwise the seed fills the gap.
          const seedSessions = SEED.sessions || [];
          db[lsKey] = data.map(row => {
            const mapped = fromSupabase(lsKey, row);
            const seed = seedSessions.find(s => s.id === mapped.id) || {};
            const merged = { ...seed };
            for (const [k, v] of Object.entries(mapped)) {
              if (v !== null && v !== undefined && v !== "") merged[k] = v;
            }
            return merged;
          });
        } else {
          db[lsKey] = data.map(row => fromSupabase(lsKey, row));
        }
        updated = true;
      }
    }
    if (updated) {
      _save(db);
      console.info("[myPT Supabase] localStorage synced from cloud.");
    }
  } catch (err) {
    console.warn("[myPT Supabase] sync failed, using localStorage cache:", err.message);
  }
}

/** Get all records from a table. */
export function getAll(table) {
  return (_load() || SEED)[table] || [];
}

/** Get a single record by id. */
export function getById(table, id) {
  return getAll(table).find(item => item.id === id) ?? null;
}

/** Filter records by a predicate function. */
export function query(table, predicate) {
  return getAll(table).filter(predicate);
}

/** Insert a new record. Auto-assigns an integer id. Returns the new record.
 *  Also fires a background Supabase insert. */
export function add(table, item) {
  const data = _load() || { ...SEED };
  const list = data[table] || [];
  const newId = list.reduce((max, i) => Math.max(max, i.id ?? 0), 0) + 1;
  const newItem = { ...item, id: newId };
  data[table] = [...list, newItem];
  _save(data);
  _sbInsert(table, newItem);  // background
  return newItem;
}

/** Merge patch fields into an existing record. Returns updated record.
 *  Also fires a background Supabase update. */
export function update(table, id, patch) {
  const data = _load() || { ...SEED };
  data[table] = (data[table] || []).map(item =>
    item.id === id ? { ...item, ...patch } : item
  );
  _save(data);
  _sbUpdate(table, id, patch);  // background
  return getById(table, id);
}

/** Delete a record by id.
 *  Also fires a background Supabase delete. */
export function remove(table, id) {
  const data = _load() || { ...SEED };
  data[table] = (data[table] || []).filter(item => item.id !== id);
  _save(data);
  _sbDelete(table, id);  // background
}

/** Hard-reset DB to seed data (dev/testing use). */
export function resetDB() {
  _save(SEED);
}

// ── SYSTEM CONTRACT OPERATIONS ────────────────────────────────────────────────
// These implement the post-conditions / exceptions from the myPT system
// contracts (Create Exercise, Create Fitness Plan, Create Session Booking,
// Record Session Performance) and act as the canonical write-path for each
// use case. Views should use these in preference to raw add/update.

// ---- Create Exercise UC ----

/**
 * System contract: createExercise (exercise details)
 *   Responsibility: Add an exercise record to the system, setting values for
 *   the attributes. Pre: PT in context. Post: exercise instance created,
 *   attributes set.
 */
export function createExercise(ptId, details) {
  if (!ptId) throw new Error("createExercise: PT must be in context");
  const draft = {
    ptId,
    name: details.name || "",
    description: details.description || "",
    teachingPoints: details.teachingPoints || "",
    adaptations: details.adaptations || "",
    bodyArea: details.bodyArea || "",
    muscle: details.muscle || "",
    category: details.category || "",
    media: details.media || "",
    videoUrl: details.videoUrl || "",
    inLibrary: false,
    source: "custom",   // PT created this — eligible to share
  };
  return add("exercises", draft);
}

/**
 * System contract: addExercise()
 *   Responsibility: Record the new exercise into the system.
 *   Exception: If the exercise name is not unique when reviewed against the
 *   other exercises associated with the PT, report an error.
 *   Post: the exercise is associated to the PT and stored in the system.
 */
export function addExerciseToLibrary(exerciseId) {
  const ex = getById("exercises", exerciseId);
  if (!ex) throw new Error("addExerciseToLibrary: exercise not found");
  const clash = query("exercises", e =>
    e.ptId === ex.ptId && e.id !== ex.id &&
    (e.name || "").trim().toLowerCase() === (ex.name || "").trim().toLowerCase()
  );
  if (clash.length) {
    throw new Error(`addExerciseToLibrary: exercise name "${ex.name}" is not unique for this PT`);
  }
  return update("exercises", exerciseId, { inLibrary: true });
}

/**
 * Update an exercise's editable fields.
 * Enforces the same unique-name-per-PT exception as addExercise().
 */
export function updateExercise(exerciseId, patch) {
  const ex = getById("exercises", exerciseId);
  if (!ex) throw new Error("updateExercise: exercise not found");
  const nextName = (patch.name ?? ex.name ?? "").trim().toLowerCase();
  if (nextName) {
    const clash = query("exercises", e =>
      e.ptId === ex.ptId && e.id !== ex.id &&
      (e.name || "").trim().toLowerCase() === nextName
    );
    if (clash.length) {
      throw new Error(`updateExercise: exercise name "${patch.name}" is not unique for this PT`);
    }
  }
  // Any edit to a non-custom exercise promotes it to the PT's personal copy (amended).
  // "custom" exercises stay custom; amended stays amended.
  const appliedPatch = { ...patch };
  if (ex.source !== "custom" && ex.source !== "amended") {
    appliedPatch.source = "amended";
  }
  return update("exercises", exerciseId, appliedPatch);
}

/**
 * Bulk-import exercises from an open-source library.
 * Skips entries whose name already exists for the PT (no exception raised).
 * Each item can include name, description, teachingPoints, bodyArea, muscle,
 * category, media, videoUrl. Returns the count of imported records.
 */
export function importExerciseLibrary(ptId, items) {
  if (!ptId) throw new Error("importExerciseLibrary: PT must be in context");
  if (!Array.isArray(items)) return 0;
  const existing = new Set(
    query("exercises", e => e.ptId === ptId).map(e => (e.name || "").trim().toLowerCase())
  );
  let count = 0;
  for (const it of items) {
    const name = (it.name || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (existing.has(key)) continue;
    existing.add(key);
    add("exercises", {
      ptId,
      name,
      description: it.description || "",
      teachingPoints: it.teachingPoints || "",
      adaptations: it.adaptations || "",
      bodyArea: it.bodyArea || "",
      muscle: it.muscle || "",
      category: it.category || "",
      media: it.media || "",
      videoUrl: it.videoUrl || "",
      inLibrary: true,
      source: it.source || "seed",  // "seed" | "wger" — not shareable until renamed
    });
    count++;
  }
  return count;
}

// ---- Create Fitness Plan UC ----

/**
 * System contract: addPlanDetails(name, objective)
 *   Capture fitness plan name and objective information.
 *   Exception: plan name must be unique to the PT.
 *   Post: fitness plan name and objective attributes set.
 */
export function createFitnessPlan(ptId, name, objective, type = "Custom") {
  if (!ptId) throw new Error("createFitnessPlan: PT must be in context");
  const clash = query("plans", p =>
    p.ptId === ptId && (p.name || "").trim().toLowerCase() === (name || "").trim().toLowerCase()
  );
  if (clash.length) throw new Error(`createFitnessPlan: plan name "${name}" is not unique for this PT`);
  return add("plans", {
    ptId, name, objective, type,
    exercises: [],         // legacy summary (names only)
    plannedExercises: [],  // richer list of planned-exercise objects
  });
}

/**
 * System contract: selectExercise(exerciseID)
 *   Responsibility: Add specified exercise to fitness plan, display the
 *   current state of the plan.
 *   Post: a planned exercise instance is created; supertype attributes are
 *   set from the exercise library; the planned exercise is associated to
 *   the fitness plan.
 */
export function selectExerciseForPlan(planId, exerciseId) {
  const plan = getById("plans", planId);
  const ex = getById("exercises", exerciseId);
  if (!plan) throw new Error("selectExerciseForPlan: plan not found");
  if (!ex)   throw new Error("selectExerciseForPlan: exerciseID does not exist");
  const planned = {
    id: Date.now() + Math.floor(Math.random() * 1000),
    exerciseId: ex.id,
    name: ex.name,
    description: ex.description,
    teachingPoints: ex.teachingPoints,
    bodyArea: ex.bodyArea,
    muscle: ex.muscle,
    planSection: "main",   // default
    metrics: [],           // { value, unit }
    note: "",
  };
  const nextPlanned = [...(plan.plannedExercises || []), planned];
  const nextNames = [...(plan.exercises || []), ex.name];
  update("plans", planId, { plannedExercises: nextPlanned, exercises: nextNames });
  return planned;
}

/**
 * System contract: assignPlanSection(plansectionID)
 *   Post: the plansection attribute is set in the planned exercise instance.
 */
export function assignPlanSection(planId, plannedId, section) {
  const plan = getById("plans", planId);
  if (!plan) throw new Error("assignPlanSection: plan not found");
  const plannedExercises = (plan.plannedExercises || []).map(pe =>
    pe.id === plannedId ? { ...pe, planSection: section } : pe
  );
  update("plans", planId, { plannedExercises });
}

/**
 * System contract: addExerciseMetric(value, unit)
 *   Set exercise metrics required for this fitness plan (e.g. reps, weight).
 */
export function addExerciseMetric(planId, plannedId, value, unit) {
  const plan = getById("plans", planId);
  if (!plan) throw new Error("addExerciseMetric: plan not found");
  const plannedExercises = (plan.plannedExercises || []).map(pe =>
    pe.id === plannedId
      ? { ...pe, metrics: [...(pe.metrics || []), { value, unit }] }
      : pe
  );
  update("plans", planId, { plannedExercises });
}

/**
 * System contract: addExerciseNote(description)
 *   Add notes defining required information to perform each exercise.
 */
export function addExerciseNote(planId, plannedId, note) {
  const plan = getById("plans", planId);
  if (!plan) throw new Error("addExerciseNote: plan not found");
  const plannedExercises = (plan.plannedExercises || []).map(pe =>
    pe.id === plannedId ? { ...pe, note } : pe
  );
  update("plans", planId, { plannedExercises });
}

// ---- Create Session Booking UC ----

/**
 * System contract: selectClient (ClientId) + selectPlan (PlanId) + addBooking
 * (Date). Combines the session-booking contract operations into a single
 * transactional call so the UI can present it as one action.
 *
 * Exceptions:
 *   - client doesn't exist
 *   - plan doesn't exist
 *   - booking date earlier than today
 */
export function createSessionBooking(ptId, clientId, planId, date, time, sessionAttributes = {}) {
  if (!ptId)    throw new Error("createSessionBooking: PT must be in context");
  const client = getById("clients", clientId);
  if (!client)  throw new Error("createSessionBooking: client id doesn't exist");
  const plan = getById("plans", planId);
  if (!plan)    throw new Error("createSessionBooking: plan id doesn't exist");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const booking = new Date(date);
  if (isNaN(booking.getTime()) || booking < today) {
    throw new Error("createSessionBooking: the date is earlier than today");
  }
  // increment session number for the client
  const prev = query("sessions", s => s.clientId === clientId);
  const sessionNumber = prev.reduce((max, s) => Math.max(max, s.sessionNumber || 0), 0) + 1;

  const session = add("sessions", {
    clientId, ptId, planId,
    sessionNumber,
    date: typeof date === "string" ? date : booking.toLocaleDateString("en-GB"),
    time: time || "—",
    name: plan.name,
    status: "scheduled",
    sessionAttributes,    // from customisePlan
    comments: [],
    performance: [],
  });
  add("upcomingSessions", {
    date: session.date, name: plan.name, time: session.time,
    clientId, sessionId: session.id,
  });
  return session;
}

/**
 * System contract: customisePlan(SessionAttributes)
 *   Post: fitness plan attributes are updated (for this session only).
 */
export function customiseSessionPlan(sessionId, sessionAttributes) {
  const s = getById("sessions", sessionId);
  if (!s) throw new Error("customiseSessionPlan: session not found");
  if (!sessionAttributes) throw new Error("customiseSessionPlan: session attributes are not set");
  return update("sessions", sessionId, { sessionAttributes });
}

// ---- Record Session Performance UC ----

/**
 * System contract: selectSession(sessionID) + addPerformanceData (metric,
 * record). Records metric + record for a given exercise in the session.
 * Pre: the physical session has been completed.
 */
export function addPerformanceData(sessionId, exerciseId, metric, record) {
  const s = getById("sessions", sessionId);
  if (!s) throw new Error("addPerformanceData: sessionID doesn't exist");
  const ex = getById("exercises", exerciseId);
  if (!ex) throw new Error("addPerformanceData: exerciseID doesn't exist");
  const entry = { exerciseId, exerciseName: ex.name, metric, record, ts: Date.now() };
  const performance = [...(s.performance || []), entry];
  const patch = { performance };
  if (s.status === "scheduled") patch.status = "awaiting-pt-review";
  return update("sessions", sessionId, patch);
}

// ---- Add Session Comments UC ----

/**
 * System contract: add comments to a completed session (UC Add Session
 * Comments). Actor may be PT or Client; both are allowed.
 */
export function addSessionComment(sessionId, author, text) {
  const s = getById("sessions", sessionId);
  if (!s) throw new Error("addSessionComment: session not found");
  const comment = { author, text, ts: Date.now() };
  const comments = [...(s.comments || []), comment];
  return update("sessions", sessionId, { comments });
}

// ---- Register Client UC (PAR-Q) ----

/**
 * Register a new client including PAR-Q responses. If any PAR-Q answer is
 * "yes" the registration is ended per the alternative flow.
 */
export function registerClient(ptId, details, parq) {
  if (!ptId) throw new Error("registerClient: PT must be in context");
  const anyYes = Object.values(parq || {}).some(v => v === true || v === "yes");
  if (anyYes) {
    throw new Error("registerClient: PAR-Q flagged — registration ended; GP clearance required");
  }
  const client = add("clients", {
    ptId,
    name: details.name || "",
    email: details.email || "",
    phone: details.phone || "",
    objectives: details.objectives || "",
    weight: Number(details.weight) || 0,
    height: Number(details.height) || 0,
    status: "awaiting-signup",
    parq: { ...parq, completedAt: Date.now() },
    clientKey: Math.random().toString(36).slice(2, 10).toUpperCase(),
    nextSession: null,
    msgs: 0,
  });
  return client;
}


// ---- Messaging ----

/**
 * Create a new message thread between two participants.
 * senderName: full name of the sender
 * recipientName: full name of the recipient (or "All Clients")
 * subject: thread subject
 * text: first message body
 */
export function createThread(senderName, recipientName, subject, text) {
  if (!senderName || !recipientName) throw new Error("createThread: sender and recipient required");
  if (!subject.trim()) throw new Error("createThread: subject is required");
  if (!text.trim()) throw new Error("createThread: message body is required");
  return add("messages", {
    subject: subject.trim(),
    participants: [senderName, recipientName],
    thread: [{ poster: senderName, text: text.trim(), ts: Date.now() }],
  });
}

/**
 * Append a reply to an existing thread.
 * senderName must be one of the thread's participants.
 */
export function replyToThread(threadId, senderName, text) {
  const thread = getById("messages", threadId);
  if (!thread) throw new Error("replyToThread: thread not found");
  if (!text.trim()) throw new Error("replyToThread: reply cannot be empty");
  const msgs = [...(thread.thread || []), { poster: senderName, text: text.trim(), ts: Date.now() }];
  return update("messages", threadId, { thread: msgs });
}

/**
 * Return all threads that include a given participant name.
 */
export function getThreadsForUser(name) {
  return query("messages", t => Array.isArray(t.participants) && t.participants.includes(name));
}
